// src/pages/Chat.js

import React, { useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

// Контекст и конфигурация
import AuthContext from '../context/AuthContext';

// Основные компоненты страницы
import GlobalFileDrop from '../components/GlobalFileDrop';
import JobStatus from '../components/JobStatus';
import ChatSidebar from '../components/Chat/ChatSidebar';
import ChatHeader from '../components/Chat/ChatHeader';
import MessageList from '../components/Chat/MessageList';
import ChatInput from '../components/Chat/ChatInput';

// Модальные окна
import CreateProjectModal from '../components/CreateProjectModal'; // Убедитесь, что этот компонент использует наш исправленный код
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import ChatSelectionModal from '../components/ChatSelectionModal';

// Стили
import '../styles/modals.css';

// Хуки с логикой
import useWebSocket from '../hooks/useWebSocket';
import useProjects from '../hooks/useProjects';
import useChats from '../hooks/useChats';
import useChatActions from '../hooks/useChatActions';

const Chat = () => {
  // --- БАЗОВЫЕ СОСТОЯНИЯ И ХУКИ ---
  const { user } = useContext(AuthContext);
  const [sidebarVisible, setSidebarVisible] = useState(() => JSON.parse(localStorage.getItem('sidebarVisible') ?? 'true'));
  const [templates, setTemplates] = useState([]);
  const [apiModels, setApiModels] = useState([]);

  // Состояния, поднятые из useChatActions для корректной перерисовки
  const [message, setMessage] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);

  // WebSocket для статуса задач
  const { isConnected, jobs, cancelJob } = useWebSocket(user?.token);

  // Кастомные хуки для основной логики
  const projectHooks = useProjects();
  const chatHooks = useChats(apiModels);
  
  const handleSidebarToggle = (visible) => {
    setSidebarVisible(visible);
    localStorage.setItem('sidebarVisible', JSON.stringify(visible));
  };
  
  const chatActions = useChatActions(
    chatHooks.currentChat, 
    templates, 
    chatHooks.actions.updateCurrentChat, 
    handleSidebarToggle,
    message,
    setMessage,
    attachedFiles,
    setAttachedFiles
  );
  
  // --- ЗАГРУЗКА ВСПОМОГАТЕЛЬНЫХ ДАННЫХ ---
  useEffect(() => {
    const fetchAuxData = async () => {
      try {
        const [templatesRes, modelsRes] = await Promise.all([
          axios.get('/api/prompts'),
          axios.get('/api/models/active')
        ]);
        setTemplates(templatesRes.data);
        setApiModels(modelsRes.data);
      } catch (error) {
        console.error("Ошибка при загрузке шаблонов или моделей", error);
      }
    };
    fetchAuxData();
  }, []);
  
  // --- ЛОГИКА, СВЯЗЫВАЮЩАЯ ХУКИ ---
  const handleDeleteProjectAndRefreshChats = async () => {
      const deletedProjectId = await projectHooks.modals.delete.confirm();
      if (deletedProjectId) {
          chatHooks.fetchChats();
      }
  };

  // --- ЛОГИКА ДЛЯ FILE DROP ---
  const [showChatSelectionModal, setShowChatSelectionModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const handleFileDrop = (fileData, prompt = '') => {
    setPendingFile(fileData);
    setShowChatSelectionModal(true);
  };
  
  const handleChatSelectionConfirm = async (option) => {
    if (!pendingFile) return;

    if (option === 'current' && chatHooks.currentChat) {
      setAttachedFiles(prev => [...prev, pendingFile]);
    } else if (option === 'new') {
      const newChat = await chatHooks.actions.createNewChat({ title: `Чат для ${pendingFile.name}` });
      if (newChat) {
        setTimeout(() => {
            setAttachedFiles([pendingFile]);
        }, 100);
      }
    }
    setPendingFile(null);
    setShowChatSelectionModal(false);
  };

  const hasActiveFileProcessingJobs = jobs?.some(job => 
    (job.status === 'queued' || job.status === 'active') && 
    ['audio', 'video', 'document'].includes(job.queue)
  );

  const handleCancelAllFileProcessing = () => {
    jobs.forEach(job => {
      if ((job.status === 'queued' || job.status === 'active') && ['audio', 'video', 'document'].includes(job.queue)) {
        cancelJob(job.jobId, job.queue);
      }
    });
  };
  
  useEffect(() => {
    document.body.classList.add('chat-page');
    const isDualMode = chatActions.dualStreaming.isDualStreaming || chatActions.dualStreaming.pendingDualChoice;
    if (isDualMode) {
      document.body.classList.add('dual-fullscreen-mode');
    } else {
      document.body.classList.remove('dual-fullscreen-mode');
    }
    return () => {
      document.body.classList.remove('chat-page');
      document.body.classList.remove('dual-fullscreen-mode');
    };
  }, [chatActions.dualStreaming.isDualStreaming, chatActions.dualStreaming.pendingDualChoice]);
  
  // При переходе в чат автоматически сворачиваем раскрытое меню навбара (mobile)
  useEffect(() => {
    const collapseEl = document.getElementById('navbarNav');
    const togglerBtn = document.querySelector('.navbar-toggler');
    if (collapseEl?.classList.contains('show')) {
      collapseEl.classList.remove('show');
      if (togglerBtn) {
        togglerBtn.setAttribute('aria-expanded', 'false');
      }
    }
  }, []);
  
  // Очистка прикреплённых, но ещё не отправленных файлов при смене чата
  useEffect(() => {
    // При изменении текущего чата сбрасываем локальные прикреплённые файлы
    setAttachedFiles([]);
  }, [chatHooks.currentChat?._id]);
  
  if (chatHooks.loading) {
    return <div className="d-flex justify-content-center align-items-center vh-100">Загрузка...</div>;
  }

  return (
    <GlobalFileDrop onFileAttach={handleFileDrop} onError={chatActions.handleUploadError}>
      <div className={`chat-container ${sidebarVisible ? '' : 'sidebar-hidden'} ${chatActions.dualStreaming.isDualStreaming || chatActions.dualStreaming.pendingDualChoice ? 'dual-mode-active' : ''}`}>
        
        {!sidebarVisible && (
          <button className="floating-sidebar-toggle" onClick={() => handleSidebarToggle(true)} title="Показать боковую панель">
            <FontAwesomeIcon icon={faBars} />
          </button>
        )}
        
        <ChatSidebar
          visible={sidebarVisible}
          onToggle={() => handleSidebarToggle(false)}
          projects={projectHooks.projects}
          chats={chatHooks.chats}
          currentChatId={chatHooks.currentChat?._id}
          projectHooks={projectHooks}
          chatHooks={chatHooks}
          hasNextPage={chatHooks.hasNextPage}
          isFetchingMore={chatHooks.isFetchingMore}
          loadMoreChats={chatHooks.loadMoreChats}
        />

        <div className="chat-history">
          {chatHooks.currentChat ? (
            <>
              {sidebarVisible && (
                <ChatHeader
                  title={chatHooks.currentChat.title}
                  editor={chatHooks.titleEditor}
                />
              )}
              
              <MessageList
                currentChat={chatHooks.currentChat}
                streamingChat={chatActions.streamingChat}
                dualStreaming={chatActions.dualStreaming}
                onUpdateChat={chatHooks.actions.updateCurrentChat}
              />
              
              <ChatInput
                chatActions={chatActions}
                attachedFiles={attachedFiles}
                setAttachedFiles={setAttachedFiles}
                message={message}
                setMessage={setMessage}
                currentChat={chatHooks.currentChat}
                templates={templates}
                apiModels={apiModels}
                hasActiveFileProcessingJobs={hasActiveFileProcessingJobs}
                onCancelAllFileProcessing={handleCancelAllFileProcessing}
                onUpdateChat={chatHooks.actions.updateCurrentChat}
              />
            </>
          ) : (
            <div className="text-center py-5 d-flex align-items-center justify-content-center h-100">
              <p className="lead">Выберите чат или создайте новый</p>
            </div>
          )}
        </div>
        
        {/* ====================================================================== */}
        {/* ИЗМЕНЕНИЕ: Используем spread-оператор для передачи всех пропсов */}
        {/* Эта строка теперь правильно подключает модальное окно к нашему хуку */}
        {/* ====================================================================== */}
        <CreateProjectModal {...projectHooks.createModalProps} />

        <ConfirmDeleteModal
          show={projectHooks.modals.delete.isOpen}
          onCancel={projectHooks.modals.delete.close}
          onConfirm={handleDeleteProjectAndRefreshChats}
          title="Удалить проект"
          message="Вы уверены, что хотите удалить этот проект?"
          description="Все чаты будут перемещены в раздел 'Чаты'. Это действие нельзя отменить."
        />

        <ConfirmDeleteModal
          show={chatHooks.deleteModal.isOpen}
          onCancel={chatHooks.deleteModal.close}
          onConfirm={chatHooks.deleteModal.confirm}
          title="Удалить чат"
          message="Вы уверены, что хотите удалить этот чат?"
          description="Все сообщения в этом чате будут потеряны навсегда. Это действие нельзя отменить."
        />

        <ChatSelectionModal
          isOpen={showChatSelectionModal}
          onClose={() => setShowChatSelectionModal(false)}
          onChatSelect={handleChatSelectionConfirm}
          fileInfo={pendingFile}
          currentChatName={chatHooks.currentChat?.title || 'Текущий чат'}
          hasExistingMessages={!!chatHooks.currentChat?.messages?.length}
        />

        {jobs?.length > 0 && (
          <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1050 }}>
            <JobStatus jobs={jobs} onCancelJob={cancelJob} isConnected={isConnected} />
          </div>
        )}
      </div>
    </GlobalFileDrop>
  );
};

export default Chat;